import type { EntityMatch } from '../types/entity-types';

interface Variation { name?: string; sourceDocument?: string; matchDetails?: EntityMatch[] }

interface EntityMatchEvidence {
  matchDetails?: EntityMatch[];
  variations?: Variation[];
}

export interface MatchSighting { name: string; document?: string }
export type GroupedEntityMatch = EntityMatch & { foundIn: MatchSighting[] };

const RICH_LISTS = ['topics', 'countries', 'positions', 'programs', 'sanctions', 'birthDate'] as const;
const union = (...lists: Array<string[] | undefined>) => [...new Set(lists.flatMap(list => list ?? []))];

/**
 * One entry per sanctions record. Each document's spelling of a party is
 * screened separately, so the same record comes back once per spelling; merge
 * those into the best score, every dataset and list value, and where it was found.
 */
export function getEntityMatchDetails(entity: EntityMatchEvidence): GroupedEntityMatch[] {
  const found: Array<{ match: EntityMatch; sighting?: MatchSighting }> = Array.isArray(entity.matchDetails)
    ? entity.matchDetails.map(match => ({ match }))
    : (entity.variations ?? []).flatMap(variation => (variation.matchDetails ?? []).map(match => ({
      match, sighting: variation.name?.trim() ? { name: variation.name.trim(), document: variation.sourceDocument } : undefined,
    })));
  const groups = new Map<string, GroupedEntityMatch>();
  for (const { match, sighting } of found) {
    const key = match.richDetails?.id || JSON.stringify(match);
    const current = groups.get(key);
    if (!current) {
      groups.set(key, { ...match, foundIn: sighting ? [sighting] : [] });
      continue;
    }
    const best = match.confidence > current.confidence ? match : current;
    const richDetails = current.richDetails || match.richDetails ? { ...current.richDetails, ...best.richDetails } : undefined;
    if (richDetails) for (const list of RICH_LISTS) {
      const merged = union(current.richDetails?.[list], match.richDetails?.[list]);
      if (merged.length) richDetails[list] = merged;
    }
    groups.set(key, {
      ...best,
      source: union(current.source.split(', '), match.source.split(', ')).filter(Boolean).join(', '),
      ...(richDetails ? { richDetails } : {}),
      foundIn: sighting && !current.foundIn.some(seen => seen.name === sighting.name && seen.document === sighting.document)
        ? [...current.foundIn, sighting] : current.foundIn,
    });
  }
  return [...groups.values()].sort((a, b) => b.confidence - a.confidence);
}
